import { useCallback, useEffect, useState } from 'react';
import { Bot, Trash2 } from 'lucide-react';
import type { SpaceBot } from '@spawnwire/shared';
import { Avatar, Button, EmptyState, Spinner, UserBadge } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { formatTimestamp } from '../../lib/time';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { Section } from '../settings/SettingsLayout';
import { botPermissionLabels } from './BotPermissions';

/** Space settings → Bots: what's been added, by whom, with what, and a way to remove each. */
export function SpaceBots({ spaceId }: { spaceId: string }) {
  const [bots, setBots] = useState<SpaceBot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const presences = useStore((s) => s.presences);
  const load = useCallback(
    () =>
      void http
        .get<SpaceBot[]>(`/spaces/${spaceId}/bots`)
        .then(setBots)
        .catch((err) => setError(errorMessage(err))),
    [spaceId],
  );
  // Loads now, and again when the member count changes (someone else added or removed a bot).
  const memberCount = useStore((s) => s.spaces[spaceId]?.memberCount);
  useEffect(() => {
    if (memberCount !== undefined) load();
  }, [memberCount, load]);

  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!bots) return <Spinner />;
  return (
    <>
      <Section description="A bot can do only what its own role allows (it gets nothing from @everyone). Change that under Roles, where its role is marked with a bot icon. Bots are added from the invite link their developer shares.">
        {bots.length === 0 ? (
          <EmptyState icon={Bot} title="No bots yet" body="When someone with Manage space adds a bot through its invite link, it shows up here." />
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {bots.map((b) => {
              const online = (presences[b.bot.id] ?? 'offline') !== 'offline';
              const labels = botPermissionLabels(b.permissions);
              return (
                <li key={b.bot.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start">
                  <div className="flex min-w-0 flex-1 gap-3">
                    <Avatar user={b.bot} size={40} status={online ? 'online' : 'offline'} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center text-sm font-semibold text-fg">
                        <span className="truncate">{b.bot.displayName}</span>
                        <UserBadge user={b.bot} />
                      </div>
                      <div className="text-xs text-faint">
                        @{b.bot.username} {'·'} added {b.addedBy ? `by ${b.addedBy.displayName} ` : ''}
                        {formatTimestamp(b.addedAt)}
                      </div>
                      {b.bot.bio && <p className="mt-1 line-clamp-2 text-sm text-muted">{b.bot.bio}</p>}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {labels.length ? (
                          labels.map((l) => (
                            <span key={l} className="rounded bg-active px-1.5 py-0.5 text-[11px] text-muted">
                              {l}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-faint">No permissions from its role</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={Trash2}
                    className="self-start"
                    onClick={() =>
                      ui.confirm({
                        title: `Remove ${b.bot.displayName}?`,
                        body: 'It leaves the space at once and its role is deleted. Its messages stay. It can be added again later.',
                        confirmLabel: 'Remove bot',
                        danger: true,
                        onConfirm: async () => {
                          await http.del(`/spaces/${spaceId}/bots/${b.bot.id}`);
                          ui.toast(`${b.bot.displayName} was removed.`, 'success');
                          load();
                        },
                      })
                    }
                  >
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </>
  );
}
